<template>
  <v-container fluid>
    <v-card class="elevation-1 mb-6">
      <v-card-title class="pa-7">
        Přehled objednávek
        <v-spacer></v-spacer>

        <v-btn
          color="primary"
          height="40px"
          min-width="130px"
          :to="{ name: 'orders.add' }"
          depressed
        >
          Nová objednávka
        </v-btn>
      </v-card-title>
      <v-card-text>
        <OrdersFilters @filter="setFiltersAction" />

        <v-data-table
          v-model="order.selected"
          :headers="userHeadersByRole"
          :items="order.items"
          :options.sync="order.options"
          :server-items-length="order.serverItemsLength"
          :loading="order.loading"
          item-key="id"
          class="elevation-0"
          show-select
        >
          <template v-slot:item.id="{ item }">
            <router-link
              :to="{
                name: 'orders.detail',
                params: {
                  orderId: item.id,
                },
              }"
            >
              {{ item.id }}
            </router-link>
          </template>
          <template v-slot:item.createdAt="{ item }">
            <DateTime :single-line="false" :datetime="item.createdAt" />
          </template>
          <template v-slot:item.updatedAt="{ item }">
            <DateTime :single-line="false" :datetime="item.updatedAt" />
          </template>
          <template v-slot:item.type="{ item }">
            {{ item.type.text }}
          </template>
          <template v-slot:item.channel="{ item }">
            {{ item.channel.text }}
          </template>
          <template v-slot:item.statusCode="{ item }">
            <OrderStatus :status="item.statusCode" />
          </template>
          <template v-slot:item.promotion="{ item }">
            <span v-if="item.promotion || item.offer"> Ano </span>
            <span v-else> </span>
          </template>
          <template v-slot:item.customer.id="{ item }">
            <router-link
              :to="{
                name: 'customers.detail',
                params: {
                  customerId: item.customer.id,
                },
              }"
            >
              {{ item.customer.id }}
            </router-link>
          </template>
          <template v-slot:item.product.name="{ item }">
            <div v-html="item.product ? removeHtml(item.product.name) : ''"></div>
          </template>
          <template v-slot:item.revision.modifiedAt="{ item }">
            <DateTime :datetime="item.revision.modifiedAt" />
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn-toggle color="primary" dense group>
              <v-btn
                :to="{ name: 'orders.detail', params: { orderId: item.id } }"
                color="blue-grey"
                text
              >
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn color="blue-grey" text @click="removeOrder(item.id)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-btn-toggle>
          </template>
          <template v-slot:footer>
            <OrdersExportFooter v-model="orderFooter" />
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { setFilters } from '@/components/mixins/setFilters'
import OrdersFilters from '@/components/filters/OrdersFilters'
import DateTime from '@/components/DateTime'
import OrderStatus from '@/components/order/OrderStatus'
import OrdersExportFooter from '@/components/order/OrdersExportFooter'

export default {
  name: 'Orders',
  components: {
    OrdersExportFooter,
    OrderStatus,
    DateTime,
    OrdersFilters,
  },
  mixins: [setFilters],
  data() {
    return {
      order: {
        selected: [],
        headers: [
          {
            text: 'Datum',
            value: 'createdAt',
            roles: ['ROLE_ADMIN'],
            width: '150px',
          },
          {
            text: 'Id',
            value: 'id',
            roles: ['ROLE_ADMIN'],
            width: '100px',
          },
          {
            text: 'Typ',
            value: 'type',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Stav',
            value: 'statusCode',
            roles: ['ROLE_ADMIN'],
            width: '150px',
          },
          {
            text: 'Změněno',
            value: 'updatedAt',
            roles: ['ROLE_ADMIN'],
            width: '150px',
          },
          {
            text: 'Id zákazníka',
            value: 'customer.id',
            roles: ['ROLE_ADMIN'],
            sortable: false,
          },
          {
            text: 'Jméno zákazníka',
            value: 'customer.fullName',
            roles: ['ROLE_ADMIN'],
            sortable: false,
          },
          {
            text: 'Dodavatel',
            value: 'product.supplierName',
            roles: ['ROLE_ADMIN'],
            sortable: false,
          },
          {
            text: 'Produkt',
            value: 'product.name',
            roles: ['ROLE_ADMIN'],
            sortable: false,
          },
          {
            text: 'Nabídka / Propagace',
            value: 'promotion',
            roles: ['ROLE_ADMIN'],
            sortable: false,
          },
          {
            text: '',
            value: 'actions',
            width: '1px',
            sortable: false,
            roles: ['ROLE_ADMIN'],
          },
        ],
        items: [],
        options: {
          page: 1,
          itemsPerPage: 10,
          sortBy: ['createdAt'],
          sortDesc: [true],
        },
        loading: false,
        serverItemsLength: 0,
      },
      orderFooter: {
        totalElements: 0,
        selectedData: {
          params: {},
          items: [],
        },
      },
      cancel: null,
    }
  },
  computed: {
    ...mapState({
      authorities: (state) => state.auth.user.authorities,
    }),
    userHeadersByRole() {
      return this.order.headers.filter((e) => e.roles.some((e) => this.authorities.includes(e)))
    },
  },
  watch: {
    'order.options': {
      handler() {
        this.loadOrders()
      },
    },
    '$route.query': {
      handler() {
        this.loadOrders()
      },
    },
    'order.selected': {
      handler() {
        this.orderFooter.selectedData.items = this.order.selected
      },
    },
  },
  methods: {
    ...mapActions({
      searchOrders: 'order/searchOrders',
      deleteOrder: 'order/deleteOrder',
    }),
    loadOrders() {
      this.order.loading = true
      const searchParams = {
        filterParams: this.$route.query,
        options: this.order.options,
      }
      this.orderFooter.selectedData.params = searchParams
      this.searchOrders(searchParams)
        .then((response) => {
          this.orderFooter.totalElements = response.data.totalElements
          this.order.items = response.data.items
          this.order.serverItemsLength = response.data.totalElements
          this.cancel = null
        })
        .finally(() => {
          this.order.loading = false
        })
    },
    setFiltersAction(filters) {
      this.order.options.page = 1
      this.setFilters(filters)
    },
    removeHtml(value) {
      return value.replace(/<p.*>.*?<\/p>/gi, '')
    },
    removeOrder(id) {
      this.$confirm('', {
        title: 'Smazat objednávku?',
        buttonTrueText: 'Ano',
        buttonFalseText: 'Ne',
        icon: '',
        color: 'black',
      }).then((result) => {
        if (result) {
          this.order.loading = true
          this.deleteOrder(id)
            .then(() => {
              this.loadOrders()
            })
            .finally(() => {
              this.order.loading = false
            })
        }
      })
    },
  },
}
</script>
